import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import pluginDataLabels from 'chartjs-plugin-datalabels';
import { BaseChartDirective, provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { ICeldaMapaCalorSuperEjecutivo, IGraficaSuperEjecutivo } from '../super-ejecutivo.interface';
import {
  altoGraficaSuperEjecutivo,
  colorIntensidadSuperEjecutivo,
  configuracionChartSuperEjecutivo,
  esGraficaDeLienzoSuperEjecutivo,
  formatearCifraSuperEjecutivo,
  prepararGraficaSuperEjecutivo,
} from './super-ejecutivo-grafica.util';

/**
 * Dibuja una gráfica del Super Ejecutivo (requerimiento `super-ejecutivo-graficas`, RN1, RN2, RN8). Las de
 * categorías y la dispersión van en un lienzo de chart.js con la cifra en cada valor o en la leyenda; el mapa
 * de calor, el progreso y las cifras son HTML. El título siempre va arriba.
 */
@Component({
  selector: 'app-super-ejecutivo-grafica',
  templateUrl: './super-ejecutivo-grafica.component.html',
  styleUrl: './super-ejecutivo-grafica.component.css',
  imports: [BaseChartDirective],
  providers: [provideCharts(withDefaultRegisterables())],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperEjecutivoGraficaComponent {
  /** La gráfica válida del contrato, tal como la escribió el Super Ejecutivo. */
  public readonly grafica = input.required<IGraficaSuperEjecutivo>();

  /** La gráfica como se dibuja: ordenada y con "Otros" si hace falta. */
  public readonly vista = computed(() => prepararGraficaSuperEjecutivo(this.grafica()));
  public readonly chart = computed(() => {
    const vista = this.vista();

    return esGraficaDeLienzoSuperEjecutivo(vista) ? configuracionChartSuperEjecutivo(vista) : null;
  });
  public readonly alto = computed(() => altoGraficaSuperEjecutivo(this.vista()));
  public readonly plugins = [pluginDataLabels];

  /** Progreso: el porcentaje real, que se escribe junto a la cifra (RN8); su color sale del semáforo por clase CSS. */
  public readonly porcentajeProgreso = computed(() => {
    const vista = this.vista();

    return vista.tipo === 'progreso' ? Math.round((vista.valor / vista.total) * 100) : 0;
  });
  /** Ancho de la barra: el porcentaje con tope en 100, por si el valor pasa del total. */
  public readonly anchoProgreso = computed(() => Math.min(100, this.porcentajeProgreso()));

  /** Mapa de calor: cada celda con su cifra y su color respecto al valor mayor. */
  public readonly celdasMapaCalor = computed<ICeldaMapaCalorSuperEjecutivo[][]>(() => {
    const vista = this.vista();

    if (vista.tipo !== 'mapaCalor') {
      return [];
    }

    const maximo = Math.max(...vista.valores.flat());

    return vista.valores.map((fila) =>
      fila.map((valor) => ({ texto: formatearCifraSuperEjecutivo(valor), color: colorIntensidadSuperEjecutivo(valor, maximo) })),
    );
  });

  public formatear(valor: number, unidad?: string): string {
    return formatearCifraSuperEjecutivo(valor, unidad);
  }
}
